import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { useToast } from "@/components/ui/use-toast";
import { Settings } from "lucide-react";

// Helper function to convert 24-hour time to 12-hour AM/PM format
const formatTime12Hour = (time24: string): string => {
  const [hours, minutes] = time24.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
  return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
};

interface AssignStaffSheetProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: Id<"serviceLocations">;
  onStaffAssigned?: () => void;
}

export function AssignStaffSheet({
  isOpen,
  onClose,
  locationId,
  onStaffAssigned,
}: AssignStaffSheetProps) {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const [shift, setShift] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Fetch active employees
  const employees = useQuery(api.employees.getActiveEmployees) ?? [];

  // Fetch enabled shifts from the organization's configuration
  const shifts = useQuery(api.shifts.listEnabled) ?? [];

  // Assign staff mutation
  const assignStaff = useMutation(api.serviceLocations.assignStaff);

  // Get the selected employee's position
  const selectedEmployee = employees.find((emp: any) => emp._id === selectedEmployeeId);
  const position = selectedEmployee?.position || "";

  // Handle form submission
  const handleSubmit = async () => {
    if (!selectedEmployeeId || !position || !shift) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await assignStaff({
        locationId,
        employeeId: selectedEmployeeId as Id<"employees">,
        position,
        shift,
      });

      toast({
        title: "Success!",
        description: "Staff member assigned successfully",
      });

      // Call the callback to refresh data
      if (onStaffAssigned) {
        onStaffAssigned();
      }

      // Reset form
      setSelectedEmployeeId("");
      setShift("");

      onClose();
    } catch (error) {
      console.error("Error assigning staff:", error);
      toast({
        title: "Error",
        description: "Failed to assign staff member",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when sheet closes
  const handleClose = () => {
    setSelectedEmployeeId("");
    setShift("");
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Assign Staff to Location</SheetTitle>
          <SheetDescription>
            Select an employee and assign them to this service location.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="employee">
                Employee <span className="text-red-500">*</span>
              </Label>
              <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.length === 0 ? (
                    <SelectItem value="no-employees" disabled>
                      No active employees found
                    </SelectItem>
                  ) : (
                    employees.map((employee: any) => (
                      <SelectItem key={employee._id} value={employee._id}>
                        {employee.name} - {employee.position}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="position">Position</Label>
              <Input
                id="position"
                value={position}
                disabled
                placeholder={
                  selectedEmployeeId
                    ? "Position will be auto-populated from employee record"
                    : "Select an employee first"
                }
                className="bg-gray-50"
              />
              <p className="text-sm text-gray-500">
                Position is automatically populated from the employee's record
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="shift">
                  Shift <span className="text-red-500">*</span>
                </Label>
                {shifts && shifts.length === 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                    onClick={() => {
                      onClose();
                      navigate("/settings?tab=shifts");
                    }}
                  >
                    <Settings className="mr-1 h-3 w-3" />
                    Configure Shifts
                  </Button>
                )}
              </div>
              <Select value={shifts && shifts.length === 0 ? "" : shift} onValueChange={setShift}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      shifts === undefined
                        ? "Loading shifts..."
                        : shifts.length === 0
                          ? "No shifts configured"
                          : "Select shift"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {shifts && shifts.length > 0 ? (
                    shifts.map((shiftOption: any) => {
                      // Format the display text based on whether the shift has a time range
                      const displayText =
                        shiftOption.startTime && shiftOption.endTime
                          ? `${shiftOption.name} (${formatTime12Hour(shiftOption.startTime)} - ${formatTime12Hour(shiftOption.endTime)})`
                          : shiftOption.name;

                      return (
                        <SelectItem key={shiftOption._id} value={shiftOption._id}>
                          {displayText}
                        </SelectItem>
                      );
                    })
                  ) : (
                    <SelectItem value="no-shifts" disabled>
                      No shifts available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1"
                disabled={isSubmitting || !selectedEmployeeId || !position || !shift}
              >
                {isSubmitting ? "Assigning..." : "Assign Staff"}
              </Button>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
